import { usePublicProjectReader, publicQueryKey, projectReaderGet } from '@/lib/public-project-reader';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost, apiPatch, apiDelete } from '@/lib/api';
import type { CustomField, CreateCustomFieldInput, UpdateCustomFieldInput } from '@/lib/schemas';

// ============================================
// QUERY KEYS
// ============================================

export const customFieldKeys = {
    all: ['customFields'] as const,
    lists: () => [...customFieldKeys.all, 'list'] as const,
    list: (projectId: string) => [...customFieldKeys.lists(), projectId] as const,
};

// ============================================
// HOOKS
// ============================================

export function useCustomFields(projectId: string) {
    const reader = usePublicProjectReader();
    return useQuery({
        queryKey: publicQueryKey(reader, customFieldKeys.list(projectId)),
        queryFn: async () => {
            const data = await projectReaderGet<{ fields: CustomField[] }>(reader, `/projects/${projectId}/custom-fields`);
            return data.fields;
        },
        refetchInterval: reader ? 10000 : false,
        enabled: !!projectId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

export function useCreateCustomField(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateCustomFieldInput) => {
            return apiPost<CustomField>(`/projects/${projectId}/custom-fields`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: customFieldKeys.list(projectId) });
        },
    });
}

export function useUpdateCustomField(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ fieldId, data }: { fieldId: string; data: UpdateCustomFieldInput }) => {
            return apiPatch<CustomField>(`/custom-fields/${fieldId}`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: customFieldKeys.list(projectId) });
        },
    });
}

export function useDeleteCustomField(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (fieldId: string) => {
            return apiDelete<{ message: string }>(`/custom-fields/${fieldId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: customFieldKeys.list(projectId) });
        },
    });
}

export function useReorderCustomFields(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (fieldIds: string[]) => {
            return apiPost<{ fields: CustomField[] }>(`/projects/${projectId}/custom-fields/reorder`, { fieldIds });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: customFieldKeys.list(projectId) });
        },
    });
}
